import { Component, ElementRef, inject, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AppConfig } from '@app/app.config';
import { IAppRequest, IAppServiceLanguages } from '@app/app.interface';
import { AppService } from '@app/app.service';
import { AppHelper } from '@app/core/app.helper';
import { FuiDestroyService } from '@services/destroy.service';
import { NotifierService } from '@services/notifier.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { filter, first, map, takeUntil } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { SideMenuService } from '../../services/side-menu.service';

@Component({
    selector: 'app-menu-edit-dialog',
    templateUrl: './menu-edit-dialog.component.html',
    styleUrls: ['./menu-edit-dialog.component.css'],
    providers: [FuiDestroyService],
})
export class MenuEditDialogComponent implements OnInit {
    readonly #destroy$: FuiDestroyService = inject(FuiDestroyService);
    /** Заголовок диалогового окна */
    dialogTitle: string = '';
    /** Текущий язык формы */
    currentLangForm: IAppServiceLanguages = {};
    /** Список мульти-язычных полей форм */
    languageFormFields = [{ name: 'name', required: true }];
    /** Форма */
    form: FormGroup = null;
    /** Ссылка на форму */
    @ViewChild('formRef', { static: false }) formRef: ElementRef;
    /** Флаг загрузки формы */
    isLoaded = false;
    constructor(
        public dialogRef: MatDialogRef<MenuEditDialogComponent>,
        public formBuilder: FormBuilder,
        public appService: AppService,
        public appConfig: AppConfig,
        public service: SideMenuService,
        public spinner: NgxSpinnerService,
        public notifier: NotifierService,
        public dialog: MatDialog,
        @Inject(MAT_DIALOG_DATA)
        public data: {
            id: any;
            data: any;
            type: string;
        },
    ) {
        this.currentLangForm = AppHelper.getCurrentLanguage(this.service);
    }
    ngOnInit(): void {
        this.dialogTitle = this.createTitle(this.data.data);
        this.createForm(this.data.data, this.data.id);
    }

    /**
     * Установить язык ввода
     * @param lang объект языка
     */
    setCurrentLangForm(lang: IAppServiceLanguages): void {
        this.currentLangForm = lang;
    }

    /**
     * Создание формы
     * @param data - данные формы
     * @param id - id пользователя
     */
    createForm(data?, id?): void {
        if (this.service.guides?.system_sections?.length) {
            this.service.guides.system_sections = [...this.service.guides.system_sections].sort((a, b) =>
                a.name.localeCompare(b.name, 'ru', { sensitivity: 'base' }),
            );
        }

        switch (this.data.type) {
            case 'controller':
                this.form = this.formBuilder.group({
                    id: [data?.id ? data.id : '', []],
                    url: [data?.url ? data.url : '', [Validators.required]],
                    css_class: [data?.css_class, []],
                    action_name: [data?.action_name ? data.action_name : '', [Validators.required]],
                    parent_id: [data?.parent_id ? data.parent_id : null, []],
                    factor_id: [null, []],
                    is_new_link: [data?.hasOwnProperty('is_new_link') ? data?.is_new_link === true : true, []],
                    is_start_page: [data?.is_start_page === true, []],
                    type: ['controller', []],
                });

                this.form
                    .get('action_name')
                    .valueChanges.pipe(
                        filter((val) => !!val && this.service.guides?.system_sections?.length),
                        map((actionName) => {
                            const list = this.service.guides.system_sections;
                            const index = list.findIndex((row) => {
                                return row.id === actionName;
                            });
                            return index < 0 ? '' : list[index]?.url;
                        }),
                        takeUntil(this.#destroy$),
                    )
                    .subscribe((val) => {
                        this.form.get('url').setValue(val);
                    });

                break;
            case 'registry':
                this.form = this.formBuilder.group({
                    id: [data?.id ? data.id : '', []],
                    url: [data?.url ? data.url : '', [Validators.required]],
                    css_class: [data?.css_class ? data.css_class : null, []],
                    action_name: ['factor_input', []],
                    parent_id: [data?.parent_id ? data.parent_id : null, []],
                    factor_id: [data?.factor_id ? data.factor_id : null, [Validators.required]],
                    is_new_link: [true, []],
                    is_start_page: [data?.is_start_page === true, []],
                    type: ['registry', []],
                });
                // Создаем ссылку на реестр при изменении значения
                this.form
                    .get('factor_id')
                    .valueChanges.pipe(takeUntil(this.#destroy$))
                    .subscribe((value) => {
                        if (value) {
                            this.form.get('url').setValue(this.service.registerLink + value);
                            const index = this.service.guides.all_registry.findIndex((x) => x.id === value);
                            // Добавляем имя реестра
                            if (index > -1 && this.service.guides.all_registry[index]?.name.length > 0) {
                                this.form
                                    .get('name')
                                    .get(this.currentLangForm.lang)
                                    .setValue(this.service.guides.all_registry[index].name);
                            }
                        }
                    });
                break;
            case 'url':
                this.form = this.formBuilder.group({
                    id: [data?.id ? data.id : '', []],
                    role_id: [data?.role_id ? data.role_id : '', [Validators.required]],
                    url: [data?.url ? data.url : '', [Validators.required]],
                    css_class: [data?.css_class ? data.css_class : null, []],
                    action_name: ['menuExternalLinks', []],
                    parent_id: [data?.parent_id ? data.parent_id : null, []],
                    factor_id: [null, []],
                    is_new_link: [true, []],
                    is_start_page: [false, []],
                    type: ['url', []],
                });
                break;
            case 'folder':
                this.form = this.formBuilder.group({
                    id: [data?.id ? data.id : '', []],
                    role_id: [data?.role_id ? data.role_id : '', [Validators.required]],
                    url: [null],
                    css_class: [data?.css_class ? data.css_class : null, []],
                    action_name: ['menuFolder', []],
                    parent_id: [data?.parent_id ? data.parent_id : null, []],
                    factor_id: [null, []],
                    is_new_link: [true, []],
                    is_start_page: [false, []],
                    type: ['folder', []],
                });
                break;
            case 'kanban':
                this.form = this.formBuilder.group({
                    id: [data?.id ? data.id : '', []],
                    role_id: [data?.role_id ? data.role_id : '', [Validators.required]],
                    url: [data?.url ? data.url : '', [Validators.required]],
                    css_class: [data?.css_class ? data.css_class : null, []],
                    action_name: ['factor_input', []],
                    priority: [data?.priority ? data.priority : 1, []],
                    parent_id: [data?.parent_id ? data.parent_id : null, []],
                    factor_id: [data?.factor_id ? data.factor_id : null, [Validators.required]],
                    is_new_link: [true, []],
                    start_page: [data?.start_page === true, []],
                    is_external_link: [false, []],
                    type: ['kanban', []],
                });
                break;
            case 'page':
                const uuid = uuidv4();
                this.form = this.formBuilder.group({
                    id: [data?.id ? data.id : '', []],
                    role_id: [data?.role_id ? data.role_id : '', [Validators.required]],
                    url: [data?.url ? data.url : 'pages/' + uuid, []],
                    css_class: [data?.css_class ? data.css_class : null, []],
                    action_name: ['fui_pages', []],
                    parent_id: [data?.parent_id ? data.parent_id : null, []],
                    factor_id: [null, []],
                    is_new_link: [true, []],
                    is_start_page: [false, []],
                    type: ['page', []],
                });
                break;
        }
        this.languageFormFields.forEach((field) => {
            const formGroup = this.formBuilder.group({});
            this.form.addControl(field.name, formGroup);
            AppHelper.createLangField(this.service, this.formBuilder, formGroup, field.name, data, field.required);
        });
        this.isLoaded = true;
    }

    /** Закрытие формы */
    close(isSave = false): void {
        this.dialogRef.close({ save: isSave, parentId: this.form.get('parent_id').value });
    }

    /** Сохранение данных */
    save(): void {
        if (this.form.valid) {
            this.spinner.show();
            const data = Object.assign({}, this.form.getRawValue());
            this.service
                .saveItem(data)
                .pipe(first(), takeUntil(this.#destroy$))
                .subscribe({
                    next: (result: IAppRequest) => {
                        this.spinner.hide();
                        if (!result.is_error) {
                            this.notifier.notify(
                                'success',
                                this.service.language_dictionary.system_data_successfully_saved,
                            );
                            this.close(true);
                        } else {
                            AppHelper.setErrorForm(result, this.form, this.formRef, this.notifier);
                        }
                    },
                    error: (err: unknown) => {
                        this.spinner.hide();
                        this.notifier.exception(err);
                    },
                });
        } else {
            AppHelper.validateAllFormFields(this.form);
            setTimeout(() => {
                const target = this.formRef.nativeElement.querySelector(
                    '.mat-select.ng-invalid, .mat-input-element.ng-invalid, .mat-checkbox-input.ng-invalid',
                );
                AppHelper.focusInput(target);
            }, 0);
        }
    }

    /**
     * Создать заголовок для диалогового окна
     * @param data - данные диалогового окна
     */
    createTitle(data: any): string {
        let title = '';
        if (data?.id && Number(data?.id) > 0) {
            title = this.appService.language_dictionary.menu_dialog_title_edit;
        } else {
            title = this.appService.language_dictionary.menu_dialog_title_add;
        }
        return title;
    }
}
